import { z } from "zod";
import { DOCUMENT_CURRENCIES } from "@/shared/lib/performance-document-currency";

export const DocumentCurrencySchema = z.enum(DOCUMENT_CURRENCIES);
export type DocumentCurrencyDTO = z.infer<typeof DocumentCurrencySchema>;

/** Money input in major units (may include decimals for USD/THB). */
export const DocumentMoneyMajorSchema = z.number().min(0);

export const DocumentLineSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  lineType: z.enum(["auto", "manual"]),
  lineCategory: z.enum(["revenue", "cost"]),
  assignmentId: z.string().nullable(),
  description: z.string(),
  quantity: z.number(),
  unit: z.string().nullable(),
  /** Minor units (integer) */
  unitPrice: z.number(),
  discountAmount: z.number(),
  subtotal: z.number(),
  taxRate: z.number(),
  taxAmount: z.number(),
  total: z.number(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DocumentLineDTO = z.infer<typeof DocumentLineSchema>;

export const InstallmentSchema = z.object({
  id: z.string(),
  quotationId: z.string().nullable(),
  invoiceId: z.string().nullable(),
  invoicedInvoiceId: z.string().nullable(),
  installmentNo: z.number(),
  label: z.string().nullable(),
  labelEn: z.string().nullable(),
  dueDate: z.coerce.date().nullable(),
  /** Minor units (integer) */
  amount: z.number(),
  receiptId: z.string().nullable(),
  status: z.enum(["pending", "paid", "overdue", "cancelled"]),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type InstallmentDTO = z.infer<typeof InstallmentSchema>;

export const DocumentSchema = z.object({
  id: z.string(),
  documentNo: z.string(),
  documentType: z.enum(["quotation", "invoice", "receipt"]),
  currency: DocumentCurrencySchema.default("LAK"),
  status: z.enum([
    "draft",
    "pending_approval",
    "approved",
    "issued",
    "partial",
    "paid",
    "voided",
  ]),
  eventId: z.string(),
  parentDocumentId: z.string().nullable(),
  issuedAt: z.coerce.date().nullable(),
  issuedBy: z.string().nullable(),
  validUntil: z.coerce.date().nullable(),
  /** Money fields below are minor units (integer) */
  subtotal: z.number(),
  discountAmount: z.number(),
  billDiscountAmount: z.number().default(0),
  taxEnabled: z.boolean().default(false),
  taxRate: z.number().default(0),
  taxAmount: z.number(),
  grandTotal: z.number(),
  paidAmount: z.number(),
  remainingAmount: z.number(),
  paymentMethod: z.enum(["cash", "transfer", "card"]).nullable(),
  installmentId: z.string().nullable(),
  sourceInstallmentId: z.string().nullable().optional(),
  paymentTerms: z.string().nullable(),
  paymentTermsEn: z.string().nullable(),
  bankAccountInfo: z.string().nullable(),
  bankAccountInfoEn: z.string().nullable(),
  notes: z.string().nullable(),
  pdfFileId: z.string().nullable(),
  billingReceiptId: z.string().nullable(),
  billingSyncedAt: z.coerce.date().nullable(),
  billingSyncedBy: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lines: z.array(DocumentLineSchema).optional(),
  installments: z.array(InstallmentSchema).optional(),
  receiptCount: z.number().optional(),
});
export type DocumentDTO = z.infer<typeof DocumentSchema>;

export const DocumentListItemSchema = DocumentSchema.omit({
  lines: true,
  installments: true,
}).extend({
  eventTitle: z.string().nullable(),
  customerName: z.string().nullable().optional(),
  eventDate: z.coerce.date().nullable(),
  dueAt: z.coerce.date().nullable().optional(),
  dueUrgency: z.enum(["ok", "warning", "overdue"]).nullable().optional(),
});
export type DocumentListItemDTO = z.infer<typeof DocumentListItemSchema>;

export const CreateDraftQuotationSchema = z.object({
  currency: DocumentCurrencySchema.default("LAK"),
});
export type CreateDraftQuotationDTO = z.input<typeof CreateDraftQuotationSchema>;

export const UpdateDocumentCurrencySchema = z.object({
  currency: DocumentCurrencySchema,
});
export type UpdateDocumentCurrencyDTO = z.infer<
  typeof UpdateDocumentCurrencySchema
>;

export const CreateManualLineSchema = z.object({
  description: z.string().min(1, "ຕ້ອງປ້ອນລາຍລະອຽດ"),
  quantity: z.number().int().min(1, "ຈຳນວນຕ້ອງຢ່າງນ້ອຍ 1"),
  unit: z.string().nullable().optional(),
  /** Major units (decimals allowed for USD/THB) */
  unitPrice: DocumentMoneyMajorSchema,
  discountAmount: DocumentMoneyMajorSchema.optional(),
});
export type CreateManualLineDTO = z.infer<typeof CreateManualLineSchema>;

export const UpdateManualLineSchema = CreateManualLineSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  "ຕ້ອງມີຢ່າງນ້ອຍ 1 ຟິວທີ່ແກ້ໄຂ",
);
export type UpdateManualLineDTO = z.infer<typeof UpdateManualLineSchema>;

export const UpdateDocumentTaxSchema = z.object({
  taxEnabled: z.boolean(),
  taxRate: z.number().int().min(0).max(100).optional(),
});
export type UpdateDocumentTaxDTO = z.infer<typeof UpdateDocumentTaxSchema>;

export const UpdateDocumentBillDiscountSchema = z.object({
  /** Major units */
  billDiscountAmount: DocumentMoneyMajorSchema,
});
export type UpdateDocumentBillDiscountDTO = z.infer<
  typeof UpdateDocumentBillDiscountSchema
>;

export const TaxSettingsSchema = z.object({
  id: z.string(),
  defaultTaxRate: z.number().int().min(0).max(100),
  updatedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type TaxSettingsDTO = z.infer<typeof TaxSettingsSchema>;

export const UpdateTaxSettingsSchema = z.object({
  defaultTaxRate: z.number().int().min(0).max(100),
});
export type UpdateTaxSettingsDTO = z.infer<typeof UpdateTaxSettingsSchema>;

export const DocumentIdParamSchema = z.object({
  documentId: z.string().min(1),
});

export const DocumentEventParamSchema = z.object({
  eventId: z.string().min(1),
  documentId: z.string().min(1),
});

export const InvoiceEventParamSchema = z.object({
  eventId: z.string().min(1),
  invoiceId: z.string().min(1),
});

export const QuotationEventParamSchema = z.object({
  eventId: z.string().min(1),
  quotationId: z.string().min(1),
});

export const CreateDocumentFromParentSchema = z.object({
  parentDocumentId: z.string().min(1),
  documentType: z.enum(["invoice", "receipt"]),
  quotationInstallmentId: z.string().min(1).optional(),
});
export type CreateDocumentFromParentDTO = z.infer<
  typeof CreateDocumentFromParentSchema
>;

export const CreatePaymentReceiptSchema = z.object({
  /** Major units */
  amount: z.number().positive(),
  installmentId: z.string().optional(),
  paymentMethod: z.enum(["cash", "transfer", "card"]).optional(),
  notes: z.string().max(500).optional(),
  label: z.string().max(200).optional(),
});
export type CreatePaymentReceiptDTO = z.infer<
  typeof CreatePaymentReceiptSchema
>;

export const UpdatePaymentReceiptDraftSchema = z
  .object({
    /** Major units */
    amount: z.number().positive().optional(),
    paymentMethod: z.enum(["cash", "transfer", "card"]).optional(),
    notes: z.string().max(500).nullable().optional(),
    label: z.string().max(200).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "ຕ້ອງມີຢ່າງນ້ອຍ 1 ຟິວທີ່ແກ້ໄຂ",
  });
export type UpdatePaymentReceiptDraftDTO = z.infer<
  typeof UpdatePaymentReceiptDraftSchema
>;

export const PricingSyncResultSchema = z.object({
  document: DocumentSchema,
  warnings: z.array(
    z.object({
      assignmentId: z.string(),
      code: z.string(),
      message: z.string(),
    }),
  ),
  breakdown: z
    .array(
      z.object({
        assignmentId: z.string().optional(),
        label: z.string(),
        amount: z.number(),
      }),
    )
    .optional(),
});
export type PricingSyncResultDTO = z.infer<typeof PricingSyncResultSchema>;

export const UpdateInstallmentPlanSchema = z.object({
  installments: z
    .array(
      z.object({
        installmentNo: z.number().int().positive(),
        label: z.string().max(200).optional(),
        labelEn: z.string().max(200).optional(),
        dueDate: z.coerce.date().nullable().optional(),
        /** Major units */
        amount: z.number().positive(),
      }),
    )
    .min(1),
});
export type UpdateInstallmentPlanDTO = z.infer<
  typeof UpdateInstallmentPlanSchema
>;

export const InvoicePaymentSummarySchema = z.object({
  invoice: DocumentSchema,
  receipts: z.array(DocumentSchema),
  installments: z.array(InstallmentSchema),
  progressPercent: z.number(),
});
export type InvoicePaymentSummaryDTO = z.infer<
  typeof InvoicePaymentSummarySchema
>;

export const QuotationInstallmentPlanSchema = z.object({
  quotation: DocumentSchema,
  installments: z.array(InstallmentSchema),
});
export type QuotationInstallmentPlanDTO = z.infer<
  typeof QuotationInstallmentPlanSchema
>;

export const VoidDocumentSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});
export type VoidDocumentDTO = z.infer<typeof VoidDocumentSchema>;

export const IssueDocumentSchema = z.object({
  issuedAt: z
    .union([
      z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      z.coerce.date(),
    ])
    .optional(),
});
export type IssueDocumentDTO = z.infer<typeof IssueDocumentSchema>;

export const LineParamSchema = z.object({
  documentId: z.string().min(1),
  lineId: z.string().min(1),
});
